import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  TrendingUp, BookOpen, Calculator, Coins, BarChart3, 
  GraduationCap, Target, DollarSign, ArrowRight, Play, 
  Award, Shield, ChevronRight, ArrowUpRight, ArrowDownRight 
} from "lucide-react";
import { API } from "../App";

const LandingPage = () => {
  const [marketPreview, setMarketPreview] = useState({ crypto: [], stocks: [] });
  
  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await axios.get(`${API}/market/all`);
        setMarketPreview({
          crypto: response.data.crypto.slice(0, 4),
          stocks: response.data.stocks.slice(0, 4)
        });
      } catch (error) {
        console.error("Error fetching market preview:", error);
      }
    };
    fetchPreview();
  }, []);
  
  const features = [
    {
      icon: <BookOpen size={32} />,
      title: "Educational Resources",
      description: "Beginner-friendly breakdowns of trading concepts and terminology",
      link: "/learn",
      color: "var(--primary)"
    },
    {
      icon: <Target size={32} />,
      title: "Demo Practice",
      description: "Practice with virtual money - no real financial risk",
      link: "/trade",
      color: "var(--success)"
    },
    {
      icon: <BarChart3 size={32} />,
      title: "Market Insights",
      description: "Follow along as we explore real-time market data together",
      link: "/markets",
      color: "var(--secondary)"
    },
    {
      icon: <Calculator size={32} />,
      title: "Learning Tools",
      description: "Calculators to understand risk, returns & position sizing",
      link: "/calculators",
      color: "var(--warning)"
    },
    {
      icon: <Coins size={32} />,
      title: "Gold Trading Guide",
      description: "Educational overview of precious metals markets",
      link: "/gold-guide",
      color: "var(--gold)"
    },
    {
      icon: <Shield size={32} />,
      title: "Risk Awareness",
      description: "Learn about capital protection and risk management principles",
      link: "/learn",
      color: "var(--error)"
    }
  ];
  
  const stats = [
    { value: "10+", label: "Lessons" },
    { value: "Community", label: "Learning" },
    { value: "Demo", label: "Practice" },
    { value: "Free", label: "To Start" }
  ];
  
  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-bg">
          <img 
            src="https://images.unsplash.com/photo-1643962579745-bcaa05ffc573?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxzdG9jayUyMG1hcmtldCUyMHRyYWRlciUyMGRhcmslMjBlbnZpcm9ubWVudHxlbnwwfHx8fDE3NzA1NTk4MzV8MA&ixlib=rb-4.1.0&q=85" 
            alt="Trading Background" 
          />
          <div className="hero-overlay" />
        </div>
        <div className="container hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">BLACKGIRLTRADING-ACADEMY • @BLACKGIRLSTRADE_</span>
            <h1 className="hero-title">
              Learn Trading<br />
              <span className="gradient-text">With Me</span>
            </h1>
            <p className="hero-subtitle">
              Hi, I'm Adiajah! Join my educational community where we explore trading tools, platforms, 
              and strategies together. I'm learning in real-time and documenting everything — 
              showing that everyday people, including Black women, belong in the world of trading.
            </p>
            <p className="hero-disclaimer">
              Not financial advice. Educational content only. <Link to="/disclaimer">Read disclaimer</Link>
            </p>
            <div className="hero-cta">
              <Link to="/about" className="btn btn-primary btn-pill" data-testid="meet-adiajah-btn">
                <Play size={18} />
                Meet Adiajah
              </Link>
              <Link to="/learn" className="btn btn-secondary btn-pill" data-testid="start-learning-btn">
                Start Learning
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
          
          {/* Stats */}
          <motion.div 
            className="hero-stats"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="section features-section" data-testid="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Everything You Need to Start Trading</h2>
            <p>From complete beginner to confident trader</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={feature.link} className="feature-card card card-interactive" data-testid={`feature-${index}`}>
                  <div className="feature-icon" style={{ color: feature.color }}>
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <span className="feature-link">
                    Explore <ChevronRight size={16} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Market Preview */}
      <section className="section market-preview-section" data-testid="market-preview-section">
        <div className="container">
          <div className="section-header">
            <h2>Market Data Overview</h2>
            <p>Follow along as we explore real-time market movements together</p>
          </div>
          
          <div className="market-preview-grid">
            {/* Crypto Preview */}
            <div className="market-preview-card card">
              <div className="market-preview-header">
                <h3>Cryptocurrency</h3>
                <Link to="/markets" className="view-all-link">
                  View All <ArrowRight size={14} />
                </Link>
              </div>
              <div className="market-preview-list">
                {marketPreview.crypto.map((coin, index) => (
                  <div key={index} className="market-preview-item">
                    <div className="market-info">
                      {coin.image && <img src={coin.image} alt={coin.name} className="market-icon" />}
                      <div>
                        <span className="market-symbol">{coin.symbol}</span>
                        <span className="market-name">{coin.name}</span>
                      </div>
                    </div>
                    <div className="market-price-info">
                      <span className="market-price">${coin.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className={`market-change ${coin.change_24h >= 0 ? 'positive' : 'negative'}`}>
                        {coin.change_24h >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {Math.abs(coin.change_24h || 0).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Stocks Preview */}
            <div className="market-preview-card card">
              <div className="market-preview-header">
                <h3>Stocks</h3>
                <Link to="/markets" className="view-all-link">
                  View All <ArrowRight size={14} />
                </Link>
              </div>
              <div className="market-preview-list">
                {marketPreview.stocks.map((stock, index) => (
                  <div key={index} className="market-preview-item">
                    <div className="market-info">
                      <div className="stock-icon">{stock.symbol.charAt(0)}</div>
                      <div>
                        <span className="market-symbol">{stock.symbol}</span>
                        <span className="market-name">{stock.name}</span>
                      </div>
                    </div>
                    <div className="market-price-info">
                      <span className="market-price">${stock.price?.toFixed(2)}</span>
                      <span className={`market-change ${stock.change_24h >= 0 ? 'positive' : 'negative'}`}>
                        {stock.change_24h >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {Math.abs(stock.change_24h || 0).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section cta-section" data-testid="cta-section">
        <div className="container">
          <div className="cta-card card-glass">
            <GraduationCap size={48} className="cta-icon" />
            <h2>Ready to Start Your Learning Journey?</h2>
            <p>Join our community and explore trading concepts together. Practice with demo accounts and build your understanding at your own pace.</p>
            <div className="cta-disclaimer">
              <Shield size={16} />
              <span>Educational content only. Not financial advice. <Link to="/disclaimer">Learn more</Link></span>
            </div>
            <div className="cta-buttons">
              <Link to="/learn" className="btn btn-primary btn-pill">
                <BookOpen size={18} />
                Start Learning
              </Link>
              <Link to="/trade" className="btn btn-success btn-pill">
                <Target size={18} />
                Try Demo Trading
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <style>{`
        .landing-page {
          background: var(--bg-default);
        }
        
        /* Hero */
        .hero-section {
          position: relative;
          min-height: 90vh;
          display: flex;
          align-items: center;
          padding: 120px 0 80px;
        }
        
        .hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        
        .hero-bg img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.3;
        }
        
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(10,10,10,0.5), var(--bg-default));
        }
        
        .hero-content {
          position: relative;
          z-index: 1;
          max-width: 900px;
        }
        
        .hero-badge {
          display: inline-block;
          padding: 8px 16px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--primary-light);
          margin-bottom: 24px;
        }
        
        .hero-title {
          font-size: 4rem;
          font-weight: 900;
          line-height: 1.1;
          margin-bottom: 24px;
        }
        
        .gradient-text {
          background: linear-gradient(90deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hero-subtitle {
          font-size: 1.25rem;
          color: var(--text-secondary);
          max-width: 600px;
          margin-bottom: 32px;
        }
        
        .hero-disclaimer {
          font-size: 14px;
          color: var(--text-muted);
          margin-bottom: 24px;
        }
        
        .hero-disclaimer a {
          color: var(--primary);
          text-decoration: none;
        }
        
        .hero-disclaimer a:hover {
          text-decoration: underline;
        }
        
        .hero-cta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }
        
        .hero-stats {
          display: flex;
          gap: 48px;
          margin-top: 64px;
          padding-top: 32px;
          border-top: 1px solid var(--border);
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
        }
        
        .stat-value {
          font-family: var(--font-heading);
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-primary);
        }
        
        .stat-label {
          font-size: 14px;
          color: var(--text-muted);
        }
        
        /* Section Headers */
        .section-header {
          text-align: center;
          margin-bottom: 48px;
        }
        
        .section-header h2 {
          margin-bottom: 12px;
        }
        
        .section-header p {
          color: var(--text-secondary);
          font-size: 1.125rem;
        }
        
        /* Features */
        .features-section {
          background: var(--bg-paper);
        }
        
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        
        .feature-card {
          display: flex;
          flex-direction: column;
          text-decoration: none;
          color: inherit;
        }
        
        .feature-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-subtle);
          border-radius: 12px;
          margin-bottom: 16px;
        }
        
        .feature-card h3 {
          font-size: 1.25rem;
          margin-bottom: 8px;
        }
        
        .feature-card p {
          color: var(--text-secondary);
          font-size: 14px;
          flex: 1;
        }
        
        .feature-link {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--primary);
          font-size: 14px;
          font-weight: 500;
          margin-top: 16px;
        }
        
        /* Market Preview */
        .market-preview-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }
        
        .market-preview-card {
          padding: 0;
          overflow: hidden;
        }
        
        .market-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border);
        }
        
        .market-preview-header h3 {
          font-size: 1rem;
          font-weight: 600;
        }
        
        .view-all-link {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--primary);
          font-size: 13px;
          text-decoration: none;
        }
        
        .market-preview-list {
          padding: 8px 0;
        }
        
        .market-preview-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 24px;
          transition: background-color 0.2s;
        }
        
        .market-preview-item:hover {
          background: var(--bg-subtle);
        }
        
        .market-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .market-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }
        
        .stock-icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          border-radius: 8px;
          font-weight: 700;
          font-size: 14px;
        }
        
        .market-symbol {
          display: block;
          font-family: var(--font-mono);
          font-weight: 600;
          font-size: 14px;
        }
        
        .market-name {
          display: block;
          font-size: 12px;
          color: var(--text-muted);
        }
        
        .market-price-info {
          text-align: right;
        }
        
        .market-price {
          display: block;
          font-family: var(--font-mono);
          font-weight: 500;
          font-size: 14px;
        }
        
        .market-change {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 2px;
          font-family: var(--font-mono);
          font-size: 12px;
          font-weight: 500;
        }
        
        .market-change.positive {
          color: var(--success);
        }
        
        .market-change.negative {
          color: var(--error);
        }
        
        /* CTA Section */
        .cta-section {
          background: var(--bg-paper);
        }
        
        .cta-card {
          text-align: center;
          padding: 64px;
          border-radius: 24px;
        }
        
        .cta-icon {
          color: var(--primary);
          margin-bottom: 24px;
        }
        
        .cta-card h2 {
          margin-bottom: 16px;
        }
        
        .cta-card p {
          color: var(--text-secondary);
          max-width: 600px;
          margin: 0 auto 32px;
        }
        
        .cta-disclaimer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-muted);
          margin-bottom: 32px;
        }
        
        .cta-disclaimer a {
          color: var(--primary);
          text-decoration: none;
        }
        
        .cta-disclaimer a:hover {
          text-decoration: underline;
        }
        
        .cta-buttons {
          display: flex;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        
        @media (max-width: 1024px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem;
          }
          
          .hero-stats {
            gap: 24px;
            flex-wrap: wrap;
          }
          
          .features-grid,
          .market-preview-grid {
            grid-template-columns: 1fr;
          }
          
          .cta-card {
            padding: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;